import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlobalRadar from './GlobalRadar';

const {
  FiShield, FiCpu, FiGlobe, FiBarChart2, FiArrowRight,
  FiLock, FiTerminal, FiLink, FiDollarSign, FiCheck, FiZap
} = FiIcons;

const LandingPage = ({ onLaunch, user }) => {
  const [isAnnual, setIsAnnual] = useState(true);

  // YOUR UPDATED STRIPE PAYMENT LINK
  const STRIPE_PRO_LINK = "https://buy.stripe.com/test_28EbJ38Tgg1CbMv4y7abK00";

  const pricingTiers = [
    {
      id: 'basic',
      name: 'Basic',
      priceMonthly: '0',
      priceAnnual: '0',
      period: '',
      desc: 'Institutional-grade starter access',
      features: [
        'Standard Entity Resolution',
        '3 Full Intelligence Reports',
        'Global OSINT Data Access',
        'Source-Verified Citations'
      ],
      buttonText: 'Start for Free',
      popular: false,
      stripeUrl: '#'
    },
    {
      id: 'pro',
      name: 'Pro',
      priceMonthly: '30', // Adjusted monthly relative to annual
      priceAnnual: '25', // UPDATED PRICE
      period: '/month',
      desc: 'For high-volume professional analysts',
      features: [
        'Everything in Basic',
        'Unlimited Intelligence Reports',
        'Priority Engine Processing',
        'Advanced Risk Triangulation',
        'PDF & Presentation Exports'
      ],
      buttonText: 'Upgrade to Pro',
      popular: true,
      stripeUrl: STRIPE_PRO_LINK
    }
  ];

  return (
    <div className="relative overflow-hidden pt-16 pb-24">
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center mb-32">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
          <div className="inline-flex items-center gap-2 bg-sonar-green/10 text-sonar-green px-4 py-1.5 rounded-full text-[10px] font-bold mb-6 border border-sonar-green/30 tracking-[0.2em] uppercase">
            <SafeIcon icon={FiTerminal} className="animate-pulse" /> Intelligence Interface
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4 tracking-tight uppercase">
            Trade Credit <span className="text-sonar-green">Adjudication.</span>
          </h1>
          <p className="text-lg text-slate-400 mb-10 leading-relaxed max-w-xl">
            Investment-grade AI-powered credit analysis and autonomous credit adjudication with complete data transparency.
          </p>
          <button 
            onClick={onLaunch}
            className="px-8 py-4 bg-sonar-green text-black font-black uppercase tracking-widest rounded-xl flex items-center justify-center gap-3 hover:bg-sonar-green-glow transition-all shadow-[0_0_20px_rgba(46,204,113,0.3)] text-sm"
          >
            Launch OMEN Analysis <SafeIcon icon={FiArrowRight} />
          </button>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}>
          <GlobalRadar label="OMEN v4 — MONITORING GLOBAL ENTITIES" />
        </motion.div>
      </div>

      {!user && (
        <div className="max-w-7xl mx-auto px-4 mt-32">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6 uppercase tracking-tight">
              Select Your <span className="text-sonar-green">Access Tier.</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mb-12">
              <span className={`text-sm font-bold ${!isAnnual ? 'text-white' : 'text-slate-500'}`}>Monthly</span>
              <button onClick={() => setIsAnnual(!isAnnual)} className="w-14 h-7 bg-sonar-green/20 border border-sonar-green/50 rounded-full relative transition-colors">
                <div className={`absolute top-1 w-5 h-5 bg-sonar-green rounded-full transition-all ${isAnnual ? 'right-1' : 'left-1'}`} />
              </button>
              <span className={`text-sm font-bold flex items-center gap-2 ${isAnnual ? 'text-white' : 'text-slate-500'}`}>
                Annual <span className="text-[10px] bg-sonar-green text-black px-2 py-0.5 rounded-full uppercase tracking-widest font-black">Save 20%</span>
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingTiers.map((tier) => (
              <div 
                key={tier.id} 
                className={`p-8 rounded-3xl flex flex-col transition-all duration-300 ${
                  tier.popular 
                    ? 'border-2 border-sonar-green bg-[#0a0f1c] relative shadow-[0_0_40px_rgba(46,204,113,0.15)]' 
                    : 'border border-sonar-border bg-sonar-card hover:border-sonar-green/50'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-sonar-green text-black text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg">
                    Recommended
                  </div>
                )}
                <div className="w-12 h-12 rounded-xl bg-sonar-green/10 border border-sonar-green/30 flex items-center justify-center mb-6">
                  <SafeIcon icon={tier.id === 'pro' ? FiZap : FiDollarSign} className="text-sonar-green text-xl" />
                </div>
                <h3 className="text-2xl font-black text-white mb-2 uppercase">{tier.name}</h3>
                <div className="flex items-end gap-1 mb-6">
                  <span className="text-5xl font-black text-white">${isAnnual ? tier.priceAnnual : tier.priceMonthly}</span>
                  <span className="text-slate-500 text-sm mb-2">{tier.period}</span>
                </div>
                <ul className="space-y-4 mb-8 flex-1">
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-300 font-mono">
                      <SafeIcon icon={FiCheck} className="text-sonar-green shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                {tier.id === 'pro' ? (
                  <a 
                    href={tier.stripeUrl} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-4 bg-sonar-green text-black rounded-xl font-black uppercase tracking-widest text-xs text-center hover:bg-sonar-green-glow shadow-[0_0_20px_rgba(46,204,113,0.3)] transition-all"
                  >
                    Upgrade to Pro
                  </a>
                ) : (
                  <button 
                    onClick={onLaunch}
                    className="w-full py-4 border border-sonar-border text-white rounded-xl font-black uppercase tracking-widest text-xs hover:border-sonar-green hover:text-sonar-green transition-all"
                  >
                    Start for Free
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;